/**
 * Created by draplater on 17-7-14.
 */

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {MenuManagerWrapperRedux} from "../Menu";
import {colors} from "../utils";
import {groupBy} from "../tagUtils";
import {SentenceGroup} from "./LexiconPage";
import {connect} from "react-redux";
import {GlobalState} from "../reduxSetting";
import {Link} from 'react-router-dom';
import {Breadcrumb, Panel} from 'react-bootstrap';
import {renderPath} from "./utils";
import {ColorMapType, FrameInfo, FrameMeta, Tag} from "../problem-domain/frame";
import {Sentence} from "../problem-domain/verb";
import * as DocumentTitle from 'react-document-title';
import {FrameInfoWire} from "../bridge";

export interface CurrentFrameData {
    info: FrameInfo;
    meta: FrameMeta;
    path: string[];
}

interface FrameProps {
    frameIndex: FrameMeta,
    currentFrameData: CurrentFrameData
}

export class FramePage extends React.Component<FrameProps, {}> {
    private frameElementRef: Map<string, any> = new Map();

    constructor(props: FrameProps) {
        super(props);
    }

    static lexiconsToLinks(frameMeta: FrameMeta, basePath: string[]): Map<string, string[]> {
        /*
        return a map (lexicons of all level) -> link chain
        */
        const result: Map<string, string[]> = new Map();
        // convert child lexicons to links
        for (const i of frameMeta.lexicons) {
            const newPath = [...basePath];
            const lexicon = i.replace(".tag", "");
            newPath.push(lexicon);
            result.set(lexicon, newPath);
        }
        // convert grand... child lexicons to links
        for (const i of frameMeta.sub_frames) {
            const newPath = [...basePath];
            newPath.push(i.name);
            Array.from(FramePage.lexiconsToLinks(i, newPath).entries())
                .forEach(([lexicon, path]) => {
                    result.set(lexicon, path);
                });
        }
        return result;
    }

    static renderLemmaLink(lemma, linksMap: Map<string, string[]>): JSX.Element {
        if (linksMap.has(lemma)) {
            return <span><Link to={"/lexicon/" + linksMap.get(lemma)!.join("/")}>{lemma}</Link> &nbsp;</span>;
        } else {
            return <span>{lemma} &nbsp;</span>;
        }
    }

    static renderSubFrameLink(subFrameNames: Set<string>,
        subFrame: string, basePath: string[]): JSX.Element {
        if (subFrameNames.has(subFrame)) {
            const path = [...basePath, subFrame];
            return <span><Link to={"/frame/" + path.join("/")}>{subFrame}</Link> &nbsp;</span>;
        } else {
            return <span>{subFrame} &nbsp;</span>;
        }
    }

    static renderLemmaWithLink(lemmas, frameMeta: FrameMeta, path: string[]): JSX.Element {
        const linksMap = FramePage.lexiconsToLinks(frameMeta, path);
        return <ul>{Array.from(lemmas.entries()).map(([name, values]) =>
            <li><span>{(name ? `<${name}> ` : "")}</span>
                {values.map(x => FramePage.renderLemmaLink(x, linksMap))}</li>)}</ul>;
    }

    static renderSubFrameWithLink(subFrames, frameMeta: FrameMeta, path: string[]): JSX.Element {
        const subFrameNames = new Set(frameMeta.sub_frames.map(x => x.name));
        return <ul>{Array.from(subFrames.entries()).map(([name, values]) =>
            <li><span>{(name ? `<${name}> ` : "")}</span>
                {values.map(x => FramePage.renderSubFrameLink(subFrameNames, x, path))}</li>)}</ul>;
    }

    static renderLemma(lemmas): JSX.Element {
        return <ul>{Array.from(lemmas.entries()).map(([name, values]) =>
            <li> {(name ? `<${name}> ` : "") + values.join(",")}</li>)}</ul>;
    }

    static renderExamples(samples, colorMap): JSX.Element {
        return <ul> {samples.map(x => SentenceGroup.renderParsedSentence(x, colorMap))} </ul>;
    }

    scrollToTagDef(name) {
        const tagNode = ReactDOM.findDOMNode(this.frameElementRef.get(name)) as HTMLElement;
        window.scrollTo(0, tagNode.offsetTop);
    }

    renderTagSet(tag: Tag,
                 colorMap: ColorMapType): JSX.Element {
        const color = colorMap(tag.name);
        return (<div className="tag-info">
            <h3 style={{backgroundColor: color, display: "inline-block"}}
                ref={(x) => this.frameElementRef.set(tag.name, x)}> {tag.name}</h3>
            <h4> Definition </h4>
            <p>{tag.definition}</p>
            <h4> Lemma </h4>
            {FramePage.renderLemma(tag.lemmas)}
            <h4> Examples </h4>
            {FramePage.renderExamples(tag.samples, colorMap)}
        </div>);
    }

    renderTagSetGroup(groupName: string, tags: Tag[],
                      colorMap: ColorMapType) {
        return (
            <div className="tag-group">
                {<h2>
                    {groupName.toLowerCase() == "core" || groupName.toLowerCase() == "non-core" ?
                        groupName + " Frame Elements" : groupName} </h2>}
                {tags.map(tag => this.renderTagSet(tag, colorMap))}
            </div>
        )
    }

    renderBreadcrumb(): JSX.Element {
        let BreadcrumbItem: any = Breadcrumb.Item;
        let path = this.props.currentFrameData!.path;
        return (
            <Breadcrumb>
                <BreadcrumbItem>
                    <Link to="/"> VerbNet </Link>
                </BreadcrumbItem>
                <BreadcrumbItem>
                    <Link to="/frame-index"> Frames </Link>
                </BreadcrumbItem>
                <BreadcrumbItem>
                    <Link to={"/frame/" + path.join("/")}> {path.slice(-1)[0]} </Link>
                </BreadcrumbItem>
            </Breadcrumb>
        );
    }

    render(): JSX.Element {
        const frameIndex = this.props.frameIndex;
        const frameData = this.props.currentFrameData;
        const frameInfo = frameData.info;
        const tags = frameInfo.tags;
        const coreFE = tags.filter(x => x.type.toLowerCase() == "core");
        const linksMap = FramePage.lexiconsToLinks(frameData.meta, frameData.path);

        return <div className="frame-info">
            {this.renderBreadcrumb()}
            <h1> {frameInfo.type || "Frame"}: {frameData.info.name} </h1>

            <h4> Frame Relation </h4>
            {renderPath(frameData.path, frameIndex, false)}

            <h4> Definition </h4>
            {frameInfo.definition}

            {frameInfo.lemmas.size != 0 && <h4> Lemma </h4>}
            {FramePage.renderLemmaWithLink(frameInfo.lemmas, frameData.meta, frameData.path)}

            {frameInfo.subFrames.size != 0 && <h4> Subframes </h4>}
            {FramePage.renderSubFrameWithLink(frameInfo.subFrames, frameData.meta, frameData.path)}

            {frameData.info.semiLexicalized.size != 0 && <h4>Semi-Lexicalized Collocates </h4>}
            {Array.from(frameData.info.semiLexicalized.entries()).map(
                ([name, {tag, verbs}]) =>
                    <div>
                        <Link to={"/lexicon/" + linksMap.get(name)!.join("/")}>[{name}]</Link>
                        {/*[{tag}]*/}
                        <span>{verbs.join(" ")}</span>
                    </div>
            )}

            {coreFE.length != 0 && <h4> Core Frame Elements </h4>}
            {coreFE.map(tag =>
                <span className="tag-list-item"
                      onClick={() => {
                          this.scrollToTagDef(tag.name)
                      }}
                      style={{backgroundColor: colors[1]}}>
                            {tag.name}</span>)}

            {frameData.meta.schema ?

                (<div><h4>Conceptual Schema</h4>
                    {frameData.meta.schema.map(
                        x => <img
                            src={`./tag/${frameData.path.join("/")}/${x}`}
                            style={{maxWidth: "100%"}}/>)}</div>)
                : null}

            {frameData.meta.sentences.length ?
                <div>
                    <h4> Construction Patterns </h4>
                    {
                        (frameData.meta.sentences.map(
                            x => <SentenceGroup
                                pattern={x.name.split(",")}
                                sentences={x.example.map(y => Sentence.parseSentence(y))}
                                colorMap={frameInfo.colorFunc} totalCount={x.count}
                                percentage={x.count / frameData.meta.totalCount!}/>))
                    }
                </div> : null
            }

            {/*Frame Elements*/}
            {Array.from(groupBy(tags, (tag: Tag) => tag.type).entries()).map(
                ([groupName, tags]) => this.renderTagSetGroup(
                    groupName.replace("*", ""),
                    tags, frameInfo.colorFunc)
            )

            }
        </div>;
    }
}

interface FramePageManagedProps {
    frameIndex?: FrameMeta,
    path: string[]
}

export class FramePageManaged extends React.Component<FramePageManagedProps,
    { frameData?: CurrentFrameData, pending: boolean }> {
    constructor(props: FramePageManagedProps) {
        super(props);
        this.state = {pending: false};
    }

    componentDidMount() {
        this.getFrameDef(this.props);
    }

    componentWillReceiveProps(newProps) {
        this.getFrameDef(newProps);
    }

    componentWillUnmount() {
        this.setState({frameData: undefined});
    }

    async getFrameDef(props: FramePageManagedProps) {
        if (!props.frameIndex) {
            return;
        }
        const frameMeta = props.frameIndex.find(props.path);
        if (frameMeta === undefined) {
            alert("Unknown frame.");
            return;
        }
        if (frameMeta.def === undefined) {
            alert("This frame not defined.");
            return;
        }

        this.setState({pending: true});
        try {
            const frameInfo = await FrameInfoWire.get(props.path, frameMeta.def);
            this.setState({
                frameData:
                    {info: frameInfo, meta: frameMeta, path: this.props.path},
                pending: false
            });
        } catch (e) {
            alert(e);
            return;
        }
    }

    render() {
        if (this.props.frameIndex) {
            if (this.state.frameData) {
                return <div className="main-panel">
                    {(this.state.pending) &&
                    <div style={{
                        position: "absolute",
                        width: "100%",
                        "text-align": "center",
                    }}><span>Loading Frame...</span></div>}
                    <div style={{opacity: this.state.pending ? 0.4 : 1}}>
                        <FramePage frameIndex={this.props.frameIndex} currentFrameData={this.state.frameData}/>
                    </div>
                </div>
            } else {
                return <div className="main-panel"> Loading Frame...</div>;
            }
        } else {
            return <div className="main-panel"> Loading Frame Index...</div>;
        }
    }
}

export const FramePageManagedRedux = connect(
    (state: GlobalState, ownProps: { path: string[] }): FramePageManagedProps  => {
        return {
            frameIndex: state.index.frames.length ? FrameMeta.wrapTop(state.index.frames) : undefined,
            path: ownProps.path
        };
    }
)(FramePageManaged);

export function renderFramePageWithRoute({location}) {
    window.scrollTo(0, 0);
    const path = location.pathname
        .replace("/frame/", "")
        .split("/");
    const frameName = path.slice(-1)[0];

    return <DocumentTitle title={`${frameName} - Frame - Mandarin VerbNet`}>
        <MenuManagerWrapperRedux>
            <Panel>
                <FramePageManagedRedux path={path}/>
            </Panel>
        </MenuManagerWrapperRedux>
    </DocumentTitle>;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/FramePage.tsx